import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  IconButton,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import Table from "../../../../../components/table/table";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { ClearAllOutlined } from "@mui/icons-material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import TableHeaderTextField from "../../../../../components/table-header-text-filed/table-header-text-filed";
import { getClientUsersRequest } from "../../../../../redux/clients/slice";
import dayjs from "dayjs";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import TableHeaderSelectField from "../../../../../components/table-header-select-field/table-header-select-field";
import TableHeaderDatePicker from "../../../../../components/table-header-datepicker/table-header-datepicker";
import CreateClientUser from "../../../users/create-client-user/create-client-user";
import Notification from "../../../../../components/notification/notification";
import { sendPasswordResetEmailRequest } from "../../../../../utils/APICalls";
import ErrorNotification from "../../../../../components/error-notification/error-notification";
import DeactivateUserDialog from "../../../users/user-details/user-details-tab/deactivate-user/deactivate-user";
import ReActivateUserDialog from "../../../users/user-details/user-details-tab/re-activate-user/re-activate-user";
import Loading from "../../../../../components/loading/loading";

const StatusRenderer = ({ rowData }: any) => {
  return (
    <Box sx={{ height: 52, display: "flex", alignItems: "center" }}>
      {rowData?.isActive ? (
        <Chip
          color="success"
          icon={<CheckOutlinedIcon />}
          label="Active"
          variant="outlined"
        />
      ) : (
        <Chip
          color="error"
          icon={<CloseOutlinedIcon />}
          label="Inactive"
          variant="outlined"
        />
      )}
    </Box>
  );
};

const NameRenderer = ({ rowData, clientData, clientId }: any) => {
  const navigate = useNavigate();
  const onViewDetailsClick = () => {
    const actualClientId = clientId || clientData?.id;
    const userId = rowData?.id;

    if (actualClientId && userId) {
      navigate(`/clients/${actualClientId}/users/${userId}`, {
        state: {
          id: userId,
          breadcrumb: {
            names: {
              [actualClientId]: clientData?.clientName,
              [userId]: `${rowData?.firstName} ${rowData?.lastName}`,
            },
          },
        },
      });
    }
  };
  return (
    <Box>
      <Button
        sx={{ textTransform: "none", px: 0 }}
        onClick={onViewDetailsClick}
      >
        <Typography>
          {rowData?.firstName} {rowData?.lastName}
        </Typography>
      </Button>
    </Box>
  );
};

type props = {
  data?: any;
};

export default function Users({ data }: props) {
  const [showAddUser, setShowAddUser] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [rowData, setRowData] = useState<any>([]);
  const [filterParams, setFilterParams] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [addedUser, setAddedUser] = useState<any>();
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams();

  const { users } = useSelector((state: any) => state.clients);

  const rowCountRef = useRef(users?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (users?.totalCount !== undefined) {
      rowCountRef.current = users?.totalCount;
    }
    return rowCountRef.current;
  }, [users?.totalCount]);

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
  }, []);

  useEffect(() => {
    const actualClientId = clientId || data?.id;
    if (
      actualClientId &&
      data?.clientName &&
      location.pathname.includes("/users") &&
      !location.pathname.includes("/users/")
    ) {
      if (!location.state?.breadcrumb?.names?.[actualClientId]) {
        navigate(location.pathname, {
          state: {
            ...location.state,
            breadcrumb: {
              names: {
                [actualClientId]: data.clientName,
              },
            },
          },
          replace: true,
        });
      }
    }
  }, [clientId, data, location.pathname, location.state, navigate]);

  useEffect(() => {
    setRowData(users?.items);
  }, [users]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  const addUser = (user: any) => {
    setAddedUser(user);
    setShowAlert(true);
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
  };

  const column: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      headerClassName: "hideRightSeparator",
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <NameRenderer
          rowData={params.row}
          clientData={data}
          clientId={clientId}
        />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="name"
          sortField="name"
          headerName={params.colDef.headerName}
          width={params.colDef.width}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "username",
      headerName: "Username",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="username"
          sortField="username"
          headerName={params.colDef.headerName}
          width={params.colDef.width}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "primaryEmail",
      headerName: "Primary Email",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="primaryEmail"
          sortField="email"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "primaryPhone",
      headerName: "Primary Phone",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="primaryPhone"
          sortField="phone"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <StatusRenderer rowData={params?.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="isActive"
          sortField="status"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          values={["Active", "Inactive"]}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "roleNames",
      headerName: "Role(s)",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="roleName"
          sortField="role"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "lastLoginDateTime",
      headerName: "Last Login",
      width: 200,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY") : " ",
      renderHeader: (params) => (
        <TableHeaderDatePicker
          field="lastLogin"
          sortField="lastLogin"
          headerName={params.colDef.headerName}
          width={params.colDef.width}
          onFilterSubmit={onFilterUsers}
          showFilter={showTableFilter}
        />
      ),
    },
  ];

  useEffect(() => {
    if (rowSelectionModel.length > 0) {
      setShowActionMenu(true);
    } else {
      setShowActionMenu(false);
    }
  }, [rowSelectionModel]);

  const handleClick = () => {
    setShowAddUser(true);
  };

  const handleDeactivateOrActivateUser = () => {
    if (rowSelectionModel.length !== 1) return;

    if (isSelectedUserActive()) {
      setShowDeactivateDialog(true);
    } else {
      setShowReactivateDialog(true);
    }
  };

  const onCloseDeactivateDialog = () => {
    setShowDeactivateDialog(false);
  };

  const onCloseReactivateDialog = () => {
    setShowReactivateDialog(false);
  };

  const onDeactivateSuccess = () => {
    setNotificationMessage("User has been deactivated successfully.");
    setShowNotification(true);
    setRowSelectionModel([]);
    if (Object.keys(filterParams).length > 0) {
      dispatch(getClientUsersRequest(filterParams));
    }
  };

  const onReactivateSuccess = () => {
    setNotificationMessage("User has been activated successfully.");
    setShowNotification(true);
    setRowSelectionModel([]);
    if (Object.keys(filterParams).length > 0) {
      dispatch(getClientUsersRequest(filterParams));
    }
  };

  const onDeactivateError = (message: string) => {
    setErrorMessage(message);
    setShowErrorNotification(true);
  };

  const onReactivateError = (message: string) => {
    setErrorMessage(message);
    setShowErrorNotification(true);
  };

  const isSelectedUserActive = () => {
    if (rowSelectionModel.length !== 1) return false;
    const selectedUserId = rowSelectionModel[0];
    const selectedUser = rowData?.find(
      (user: any) => user.id === selectedUserId
    );
    return selectedUser?.isActive === true;
  };

  const handleSendPasswordResetEmail = async () => {
    if (rowSelectionModel.length !== 1) return;

    setSendingPasswordReset(true);
    try {
      const response = await sendPasswordResetEmailRequest(
        rowSelectionModel[0] as string
      );

      if (response?.status === 200 || response?.status === 204) {
        setNotificationMessage("Password reset email sent successfully.");
        setShowNotification(true);
        setRowSelectionModel([]);
      } else {
        setErrorMessage(
          response?.data?.message ||
            "Failed to send password reset email. Please try again."
        );
        setShowErrorNotification(true);
      }
    } catch (error) {
      setErrorMessage("Failed to send password reset email. Please try again.");
      setShowErrorNotification(true);
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const onClearAllFilters = () => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams({
      page: 1,
      pageSize: 10,
    } as any);
    setFilterResetKey((prev) => prev + 1);
  };

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      filters.id = clientId || data?.id;
      dispatch(getClientUsersRequest(filters));
    }
  }, [filterParams]); // eslint-disable-line

  return (
    <>
      <Box className="clients-header" sx={{ mt: 3 }}>
        {/* <TextField sx={{ width: 300 }} id="outlined-basic" label="Search" variant="outlined" placeholder="Name, Email, etc..." /> */}
        <Box></Box>
        <Button
          className="primary-bg"
          id="add-button"
          variant="contained"
          disableElevation
          onClick={handleClick}
          startIcon={<AddIcon />}
        >
          Add User
        </Button>
      </Box>

      <Box className="clients-header" sx={{ my: 1.5 }}>
        {showActionMenu ? (
          <Box display={"flex"} alignItems={"center"}>
            <Button
              variant="text"
              onClick={handleSendPasswordResetEmail}
              disabled={rowSelectionModel.length !== 1}
            >
              Send Password Reset Email
            </Button>
            <Button
              variant="text"
              onClick={handleDeactivateOrActivateUser}
              disabled={rowSelectionModel.length !== 1}
            >
              {rowSelectionModel.length === 1 && !isSelectedUserActive()
                ? "Activate User"
                : "Deactivate User"}
            </Button>
            <Divider orientation="vertical" sx={{ mx: 0.5, height: 34 }} />
            <IconButton aria-label="delete" disabled>
              <DeleteOutlinedIcon color="disabled" />
            </IconButton>
          </Box>
        ) : (
          <Typography sx={{ ml: 1 }}>
            Select items below to see the actions.
          </Typography>
        )}
        <Box display={"flex"}>
          <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
            <ClearAllOutlined color="primary" />
          </IconButton>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          key={filterResetKey}
          rowData={rowData}
          columnDef={column}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          disableColumnMenu
          headerHeight={headerHeight}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
        />
      </Box>
      <CreateClientUser
        showDialog={showAddUser}
        closeDialog={() => setShowAddUser(false)}
        addUser={addUser}
        clientId={clientId || data?.id}
      />
      <Notification
        show={showAlert}
        message={`${addedUser?.username} has been added to Spectrum.`}
        onClose={() => setShowAlert(false)}
      />

      {showNotification && (
        <Notification
          show={showNotification}
          message={notificationMessage}
          onClose={() => setShowNotification(false)}
        />
      )}

      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}

      {showDeactivateDialog && (
        <DeactivateUserDialog
          showDialog={showDeactivateDialog}
          closeDialog={onCloseDeactivateDialog}
          id={rowSelectionModel[0] as string}
          onSuccess={onDeactivateSuccess}
          onError={onDeactivateError}
        />
      )}

      {showReactivateDialog && (
        <ReActivateUserDialog
          showDialog={showReactivateDialog}
          closeDialog={onCloseReactivateDialog}
          id={rowSelectionModel[0] as string}
          onSuccess={onReactivateSuccess}
          onError={onReactivateError}
        />
      )}

      {sendingPasswordReset && <Loading />}
    </>
  );
}
